import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { i18n, useTranslation } from "./i18n";
import { hasPendingLocalRequests } from "./lib/localStore/client";
import {
  applyPwaUpdate,
  checkForPwaUpdate,
  getPwaUpdateState,
  PWA_CHECK_EVENT,
  PWA_STATUS_EVENT,
  PWA_UPDATE_EVENT,
  PwaUpdateError,
} from "./pwa";
import "./pwa-updates.css";

export default function PwaUpdateNotice() {
  const { t } = useTranslation("pwaInstall");
  const { pathname, search } = useLocation();
  const isIntroductionPage =
    ["/", "/about", "/data", "/guide", "/sync", "/ipad", "/update-check"].includes(pathname) &&
    !new URLSearchParams(search).has("preview");
  const [state, setState] = useState(getPwaUpdateState);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const previousAttention = useRef("");
  const statusPanel = useRef<HTMLDivElement>(null);
  const focusRequested = useRef(false);
  const attention = !["idle", "current", "checking"].includes(state.kind);

  useEffect(() => {
    const key = `${state.kind}:${state.availableBuild || ""}`;
    if (attention && previousAttention.current !== key) {
      previousAttention.current = key;
      setExpanded(true);
    } else if (state.kind === "current") previousAttention.current = "";
  }, [attention, state.kind, state.availableBuild]);

  useEffect(() => {
    if (expanded && focusRequested.current) {
      focusRequested.current = false;
      statusPanel.current?.focus();
    }
  }, [expanded, state.kind]);

  useEffect(() => {
    const show = () => setState({ ...getPwaUpdateState(), kind: "ready" });
    const changed = () => {
      const next = getPwaUpdateState();
      if (next.kind === "checking") setError("");
      setState(next);
    };
    const requested = () => {
      focusRequested.current = true;
      setExpanded(true);
      setError("");
      void checkForPwaUpdate();
    };
    window.addEventListener(PWA_UPDATE_EVENT, show);
    window.addEventListener(PWA_STATUS_EVENT, changed);
    window.addEventListener(PWA_CHECK_EVENT, requested);
    changed();
    return () => {
      window.removeEventListener(PWA_UPDATE_EVENT, show);
      window.removeEventListener(PWA_STATUS_EVENT, changed);
      window.removeEventListener(PWA_CHECK_EVENT, requested);
    };
  }, []);

  function check() {
    focusRequested.current = true;
    setExpanded(true);
    setError("");
    void checkForPwaUpdate();
  }

  async function update() {
    if (hasPendingLocalRequests()) {
      setError(i18n.t("screenCopy:pwaUpdateNotice.localDataIsStillBeingProcessedWait"));
      return;
    }
    if (!window.confirm(i18n.t("screenCopy:pwaUpdateNotice.saveClassroomRecordsFirstUpdatingReloadsThe"))) return;
    setBusy(true);
    setError("");
    try {
      await applyPwaUpdate();
      window.location.reload();
    } catch (caught) {
      if (caught instanceof PwaUpdateError && caught.code !== "changed") setState({ kind: caught.code });
      setError(
        caught instanceof PwaUpdateError
          ? t(caught.code === "changed" ? "versionChanged" : `status.${caught.code}`)
          : caught instanceof Error
            ? caught.message
            : i18n.t("screenCopy:pwaUpdateNotice.theUpdateIsNotReadyTryAgain"),
      );
      setBusy(false);
    }
  }

  if (isIntroductionPage) return null;
  const ready = state.kind === "ready";
  const checking = state.kind === "checking" || state.kind === "downloading";
  if (!expanded && !attention) return null;
  if (!expanded)
    return (
      <div className="pwa-update-summary">
        <span>{t("updatePending")}</span>
        <button
          type="button"
          className="pwa-update-check"
          aria-label={t("checkUpdate")}
          onClick={check}
          disabled={checking}
        >
          {t("checkUpdate")}
        </button>
      </div>
    );
  return (
    <div
      className="pwa-update-notice"
      ref={statusPanel}
      tabIndex={-1}
      role="status"
      aria-label={t("updateStatus")}
      aria-live="polite"
    >
      <div className="pwa-update-content">
        <b className="pwa-update-version">{t("versionLabel", { build: __APP_RELEASE_TAG__ })}</b>
        <span className="pwa-update-message">{error || t(`status.${state.kind}`)}</span>
      </div>
      <div className="pwa-update-actions">
        {ready && (
          <button type="button" disabled={busy || checking} onClick={() => void update()}>
            {busy
              ? i18n.t("screenCopy:pwaUpdateNotice.updating")
              : i18n.t("screenCopy:pwaUpdateNotice.updateAfterSaving")}
          </button>
        )}
        {state.kind === "auth-required" && (
          <a href="/update-check" target="_blank" rel="noopener noreferrer">
            {t("websiteSignIn")}
          </a>
        )}
        <button type="button" disabled={busy || checking} onClick={check}>
          {t("checkUpdate")}
        </button>
        <button
          type="button"
          className="pwa-update-dismiss"
          aria-label={t("closeStatus")}
          onClick={() => setExpanded(false)}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  );
}
